import { JobMedia } from '@app/models';
import { AppState } from './app.state';
import { catchError, concatMap, map, mergeMap } from 'rxjs/operators';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { Injectable } from '@angular/core';
import { createAction, props, Store } from '@ngrx/store';
import { JobMediaService } from '@services/api/job-media.service';
import { CreateSuccess } from '@briebug/ngrx-auto-entity';
import {
  ImageUploadService,
  PhotoMeta,
} from '@services/image-upload/image-upload.service';
import { hasPhotoUploadSource } from '@services/upload/photo-source';
import { UploadQueueService } from '@services/upload/upload-queue.service';
import { EMPTY } from 'rxjs';

export interface CreateJobMediaRequest {
  job: string;
  employee: string;
  fileName: string;
  photo: PhotoMeta;
}

export const createJobMedia = createAction(
  '[Job Media] Create',
  props<{ payload: CreateJobMediaRequest }>()
);

@Injectable()
export class JobMediaEffects {
  createMedia$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createJobMedia),
        map(action => action.payload),
        // `mergeMap`, not `concatMap`: the creates must not be serialised.
        // Each one is a server round-trip, so six photos took ~15 s to all
        // reach the native queue -- and a force-quit before that window
        // closed lost the ones still waiting. Crews do take a burst and
        // pocket the phone immediately. Running them together cuts the
        // window to roughly one round-trip. It does not close it; that needs
        // the photo to become durable at capture rather than at create.
        mergeMap(payload => {
          // Bail before the POST. A photo the uploader cannot read would
          // otherwise leave a permanent row with imageExists: false -- a grey
          // placeholder that never resolves, which is the exact failure this
          // whole change exists to remove.
          if (!hasPhotoUploadSource(payload.photo)) {
            console.error(
              '[Job Media] create skipped: photo has neither a native path nor a file',
              payload.fileName
            );
            return EMPTY;
          }

          const jm: JobMedia = {
            job: payload.job,
            employee: payload.employee,
            originalFileName: payload.fileName,
            imageExists: false,
            notes: '',
          };

          return this.jobMediaService.create(jm).pipe(
            // `concatMap` with an async callback, not `map`: concatMap awaits
            // the promise, so a rejection reaches the catchError below
            // instead of becoming an unhandled rejection.
            concatMap(async response => {
              this.store$.dispatch(new CreateSuccess(JobMedia, response));

              if (!this.uploads.isNativeEnabled) {
                // Legacy path, kept alive precisely for this: native code
                // cannot be hot-fixed, so the kill switch needs somewhere to
                // fall back to rather than just stopping uploads.
                const photo = { ...payload.photo, id: response.id };
                await this.imageUpload.addPhoto(photo);
                this.jobMediaService.uploadFile({
                  jobMedia: response,
                  image: await this.imageUpload.getPhotoFile(response.id),
                });
                return;
              }

              // Hand the plugin the native path. The photo is never read
              // into the WebView -- a full-resolution capture is several
              // megabytes and six of them as Blobs can take the app down.
              await this.uploads.enqueue({
                entityType: 'JobMedia',
                entityId: response.id,
                uploadUrl: response.uploadUrl,
                originalFileName: payload.fileName,
                photo: payload.photo,
              });
            }),
            catchError(error => {
              console.error('[Job Media] create failed', error);
              return EMPTY;
            })
          );
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private jobMediaService: JobMediaService,
    private uploads: UploadQueueService,
    private imageUpload: ImageUploadService,
    private store$: Store<AppState>
  ) {}
}
